import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CodedStatusMessage } from '@core/models/coded-status-message.model';
import { occurrenceText } from '@core/services/gbe-error-parser/entity-ref-labels';
import { WarningOccurrence } from '@core/services/gbe-error-parser/warning.model';
import { NotificationService } from '@core/services/notification/notification.service';

/**
 * Copying a finding to the clipboard, shared by the panel row and the card that row opens
 * so the two never drift into copying different text.
 */
@Injectable({ providedIn: 'root' })
export class FindingCopyService {
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);

  /**
   * Copies exactly the message text shown on screen. `rawPayload` copies the wire
   * message instead, for a bug report.
   */
  copy(
    occurrence: Pick<WarningOccurrence, 'body'>,
    raw: CodedStatusMessage,
    rawPayload: boolean
  ): void {
    this.write(rawPayload ? JSON.stringify(raw, null, 2) : occurrenceText(occurrence));
  }

  /**
   * Copies a finding's heading — what identifies the kind of finding rather than one of
   * its occurrences, so it can be pasted into a ticket or a search box.
   */
  copyHeading(title: string, code: string): void {
    this.write(`${title} (${code})`);
  }

  private write(content: string): void {
    navigator.clipboard.writeText(content).then(
      () =>
        this.notificationService.success(
          this.translate.instant('features.plan.check.copy_success'),
          '',
          2000
        ),
      () =>
        this.notificationService.error(
          this.translate.instant('features.plan.check.copy_error'),
          ''
        )
    );
  }
}
